// src/components/marketing/ComposePost.tsx
// Modal for composing a new ScheduledPost. Covers the 80% flow:
//   caption + hashtags + scheduled_at + platform + post_type + upload media
//
// Upload is split in two steps on the backend:
//   1. POST /admin/posts/upload-media (multipart) → returns Bunny URL
//   2. POST /admin/posts/{id}/media (attach URL) after the post is created
// We hide that from the user — the modal accepts a File, uploads it,
// creates the post, attaches the media, then reports success.

import React, { useRef, useState } from 'react';
import {
  AlertTriangle, Image as ImageIcon, Loader2, Send, Upload, X,
} from 'lucide-react';

import {
  ApiError,
  attachMedia,
  createPost,
  uploadMedia,
  type MediaUploadOut,
  type Platform,
  type PostType,
  type ScheduledPostIn,
} from '@/lib/api_social_publish';
import { useActiveBusiness } from '@/context/BusinessContext';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

interface Props {
  /** Pre-fill scheduled_at when opening from a day cell in the calendar. */
  defaultDate?: Date | null;
  onClose: () => void;
  /** Parent refreshes its list after a successful post. */
  onCreated: () => void;
  addNotification?: Notifier;
}

const PLATFORMS: Array<{ value: Platform; label: string }> = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
];

const POST_TYPES: Array<{ value: PostType; label: string }> = [
  { value: 'feed', label: 'Feed' },
  { value: 'story', label: 'Story' },
  { value: 'reel', label: 'Reel' },
];

export function ComposePost({ defaultDate, onClose, onCreated, addNotification }: Props) {
  const { activeId: activeBusinessId, active: activeBusiness } = useActiveBusiness();
  void activeBusiness; // available for future UI display
  const [platform, setPlatform] = useState<Platform>('facebook');
  const [postType, setPostType] = useState<PostType>('feed');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [scheduled, setScheduled] = useState(defaultIsoLocal(defaultDate));
  const [file, setFile] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState<MediaUploadOut | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (f: File | null) => {
    if (!f) {
      setFile(null);
      setUploaded(null);
      return;
    }
    setFile(f);
    setUploaded(null);
    setUploading(true);
    setError(null);
    try {
      const out = await uploadMedia(f);
      setUploaded(out);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error subiendo archivo.');
      setError(msg);
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const trimmedCaption = caption.trim();
    const tags = hashtags
      .split(/[\s,]+/)
      .map((t) => t.replace(/^#/, '').trim())
      .filter(Boolean);

    // Caption OR media is required — empty post makes no sense.
    if (!trimmedCaption && !uploaded) {
      setError('Agrega caption o media antes de guardar.');
      return;
    }

    // Instagram posts require media.
    if (platform === 'instagram' && !uploaded) {
      setError('Instagram requiere imagen o video.');
      return;
    }

    if (!activeBusinessId) {
      setError('Primero selecciona un negocio (Empresa → Negocios).');
      return;
    }

    const payload: ScheduledPostIn = {
      business_id: activeBusinessId,
      platform,
      post_type: postType,
      caption: trimmedCaption,
      hashtags: tags,
    };
    if (scheduled) {
      const d = new Date(scheduled);
      if (!Number.isNaN(d.getTime())) {
        payload.scheduled_at = d.toISOString();
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const post = await createPost(payload);

      // Attach the media after the post is created so we can link it.
      if (uploaded) {
        await attachMedia(post.id, {
          kind: uploaded.kind,
          media_url: uploaded.media_url,
          sort_order: 0,
        });
      }

      addNotification?.({
        type: 'success',
        title: payload.scheduled_at ? 'Post programado' : 'Borrador guardado',
        message: payload.scheduled_at
          ? `Se publicará el ${new Date(payload.scheduled_at).toLocaleString()}`
          : 'Queda como borrador — puedes agendarlo más tarde.',
        duration: 4000,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : (err?.message || 'Error creando post.');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-xl rounded-xl border border-white/10 bg-[var(--panel,#1e1f2b)] p-5 max-h-[90vh] overflow-auto"
      >
        <header className="flex items-center gap-2 mb-4">
          <h2 className="text-base font-semibold">Nuevo post</h2>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="o-btn-ghost p-1 rounded-md text-white/60 hover:text-white"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </header>

        {/* Platform + type row */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Field label="Plataforma">
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Platform)}
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none"
            >
              {PLATFORMS.map((p) => (
                <option key={p.value} value={p.value} className="bg-[var(--panel,#1e1f2b)]">
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tipo">
            <select
              value={postType}
              onChange={(e) => setPostType(e.target.value as PostType)}
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none"
            >
              {POST_TYPES.map((p) => (
                <option key={p.value} value={p.value} className="bg-[var(--panel,#1e1f2b)]">
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Caption">
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={4}
            placeholder="Texto del post…"
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          />
          <div className="text-xs text-white/40 mt-1">{caption.length.toLocaleString()} caracteres</div>
        </Field>

        <Field label="Hashtags" hint="Separados por espacio o coma. El '#' lo agrega el publisher.">
          <input
            type="text"
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            placeholder="travel adventure chiapas"
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          />
        </Field>

        <Field label="Programar para" hint="Déjalo vacío para guardar como borrador.">
          <input
            type="datetime-local"
            value={scheduled}
            onChange={(e) => setScheduled(e.target.value)}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          />
        </Field>

        {/* Media upload */}
        <Field label="Media" hint="Máx 100 MB. image/jpeg|png|webp|gif o video/mp4|quicktime|webm.">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="rounded-lg border-2 border-dashed border-white/15 bg-white/5 p-4 flex items-center gap-3"
          >
            {uploading ? (
              <>
                <Loader2 size={18} className="animate-spin opacity-70" />
                <div className="flex-1">
                  <div className="text-sm">Subiendo a Bunny…</div>
                  <div className="text-xs text-white/50 truncate">{file?.name}</div>
                </div>
              </>
            ) : uploaded ? (
              <>
                {uploaded.kind === 'image' ? (
                  <img src={uploaded.media_url} alt="preview" className="w-16 h-16 object-cover rounded-md" />
                ) : (
                  <div className="w-16 h-16 rounded-md bg-black/40 flex items-center justify-center text-xs text-white/60">
                    VIDEO
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{uploaded.original_filename}</div>
                  <div className="text-xs text-white/50">
                    {uploaded.kind} · {(uploaded.size_bytes / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleFile(null)}
                  className="text-sm text-white/50 hover:text-white"
                >
                  Quitar
                </button>
              </>
            ) : (
              <>
                <ImageIcon size={18} className="opacity-50" />
                <div className="flex-1 text-sm text-white/60">
                  Arrastra una imagen/video aquí, o
                </div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-sm flex items-center gap-1"
                >
                  <Upload size={14} />
                  Elegir archivo
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/*"
                  hidden
                  onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                />
              </>
            )}
          </div>
        </Field>

        {error && (
          <div className="mt-3 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200 flex items-start gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div className="break-words">{error}</div>
          </div>
        )}

        <footer className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 text-sm"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting || uploading}
            className="px-3 py-1.5 rounded-md bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {scheduled ? 'Programar' : 'Guardar borrador'}
          </button>
        </footer>
      </form>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block mb-3">
      <span className="text-xs uppercase tracking-wider text-white/50">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="text-xs text-white/40 mt-1 block">{hint}</span>}
    </label>
  );
}

/** Pre-populate `datetime-local` with "now + 1 hour" (or the provided day
 *  at noon) so the user doesn't have to type the full timestamp. */
function defaultIsoLocal(fromDay: Date | null | undefined): string {
  let d: Date;
  if (fromDay) {
    d = new Date(fromDay);
    d.setHours(12, 0, 0, 0);
  } else {
    d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default ComposePost;
